import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES_LIST } from '../utils/countries';
import { getDefaultLeaguePreference, validateLeaguePreferenceSelection } from '../utils/leaguePreference';
import LeaguePreferenceEditor from '../components/LeaguePreferenceEditor';
import TurnstileWidget from '../components/TurnstileWidget';
import './Auth.css';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [leaguePreference, setLeaguePreference] = useState(getDefaultLeaguePreference);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileKey((k) => k + 1);
  };

  const handleUsernameChange = (e) => {
    // Mobile keyboards capitalize the first character; store it as lowercase.
    const value = e.target.value.toLowerCase();
    // Only allow a-z, 0-9, ., _, - (lowercase letters only, no spaces)
    if (value === '' || /^[a-z0-9._-]+$/.test(value)) {
      setUsername(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate date of birth
    if (!dateOfBirth) {
      setError('Date of birth is required');
      return;
    }

    const dobDate = new Date(dateOfBirth);
    if (isNaN(dobDate.getTime())) {
      setError('Invalid date of birth');
      return;
    }

    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }

    if (age < 18) {
      setError('You must be at least 18 years old to register');
      return;
    }

    const leagueError = validateLeaguePreferenceSelection(leaguePreference);
    if (leagueError) {
      setError(leagueError);
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the captcha');
      return;
    }

    setLoading(true);
    const result = await register(
      username,
      email,
      password,
      country,
      dateOfBirth,
      leaguePreference,
      turnstileToken
    );
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 8000);
    } else {
      setError(result.error);
      resetTurnstile();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Join ScoreClash</h1>
          <p className="auth-subtitle">Lock in scorelines, swing matches with plays, and climb</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className="form-field">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-input"
              value={username}
              onChange={handleUsernameChange}
              required
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              pattern="[a-z0-9._-]+"
              title="Username can only contain lowercase letters, numbers, and special characters (., _, -). No spaces or uppercase letters allowed."
              placeholder="e.g., player123"
              data-testid="register-username"
            />
            <small className="form-hint">
              Only lowercase letters (a-z), numbers (0-9), and special characters (., _, -) allowed
            </small>
          </div>

          <div className="form-field">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength="6"
            />
          </div>

          <div className="form-field">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
            >
              <option value="">Select your country</option>
              {COUNTRIES_LIST.map(({ code, name }) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              className="form-input"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
            />
            <small className="form-hint">
              You must be at least 18 years old to register
            </small>
          </div>

          <div className="form-field">
            <label className="form-label">Leagues you follow</label>
            <LeaguePreferenceEditor
              value={leaguePreference}
              onChange={setLeaguePreference}
              disabled={loading || success}
            />
          </div>

          <TurnstileWidget key={turnstileKey} onTokenChange={setTurnstileToken} />

          {error && <div className="auth-error">{error}</div>}
          {success && (
            <div className="auth-success">
              Registration successful. We sent a verification link to your email. Open it before logging in.
              Redirecting to login…
            </div>
          )}

          <button type="submit" className="auth-btn" disabled={loading || success || !turnstileToken}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <Link to="/login" className="auth-link">Login here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Register;
